import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Property APIs
export const propertyAPI = {
  getAll: () => api.get('/properties'),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
};

// Room APIs
export const roomAPI = {
  getAll: (propertyId) => api.get(`/rooms?property_id=${propertyId}`),
  create: (data) => api.post('/rooms', data),
  setAvailable: (roomId) => api.post(`/rooms/${roomId}/set-available`),
};

// Season APIs
export const seasonAPI = {
  getAll: (propertyId) => api.get(`/seasons?property_id=${propertyId}`),
  create: (data) => api.post('/seasons', data),
};

// Rate APIs
export const rateAPI = {
  getAll: (propertyId) => api.get(`/rates?property_id=${propertyId}`),
  create: (data) => api.post('/rates', data),
};

// Reservation APIs
export const reservationAPI = {
  getAll: (propertyId) => api.get(`/reservations?property_id=${propertyId}`),
  create: (data) => api.post('/reservations', data),
  update: (id, data) => api.patch(`/reservations/${id}`, data),
  checkin: (reservationId) => api.post(`/reservations/${reservationId}/checkin`),
  checkout: (reservationId) => api.post(`/reservations/${reservationId}/checkout`),
  getWithPayments: (reservationId) => api.get(`/reservations/${reservationId}/with-payments`),
};

// Payment APIs
export const paymentAPI = {
  create: (data) => api.post('/payments', data),
  getByReservation: (reservationId) => api.get(`/payments/reservation/${reservationId}`),
  getByProperty: (propertyId) => api.get(`/payments/property/${propertyId}`),
  delete: (paymentId) => api.delete(`/payments/${paymentId}`),
};

// Guest APIs
export const guestAPI = {
  getAll: (propertyId) => api.get(`/guests?property_id=${propertyId}`),
};

// OTA Connection APIs
export const otaAPI = {
  getAll: (propertyId) => api.get(`/ota-connections?property_id=${propertyId}`),
  create: (data) => api.post('/ota-connections', data),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: (propertyId) => api.get(`/dashboard/stats?property_id=${propertyId}`),
};

// Admin APIs
export const adminAPI = {
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  deleteProperty: (propertyId) => api.delete(`/admin/properties/${propertyId}`),
};

// WhatsApp Chatbot APIs
export const whatsappAPI = {
  getConfig: (propertyId) => api.get(`/whatsapp/config/${propertyId}`),
  saveConfig: (data) => api.post('/whatsapp/config', data),
  testChat: (data) => api.post('/whatsapp/test-chat', data),
  getStatus: (propertyId) => api.get(`/whatsapp/status/${propertyId}`),
  getAllConfigs: () => api.get('/admin/whatsapp/configs'),
};

// Catalog APIs
export const catalogAPI = {
  get: (propertyId) => api.get(`/catalog/${propertyId}`),
  save: (propertyId, data) => api.put(`/catalog/${propertyId}`, data),
  uploadPhoto: (propertyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/catalog/${propertyId}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePhoto: (propertyId, photoId) => api.delete(`/catalog/${propertyId}/photo/${photoId}`),
};

// Sub-User Management APIs
export const subUserAPI = {
  getAll: () => api.get('/sub-users'),
  getById: (userId) => api.get(`/sub-users/${userId}`),
  create: (data) => api.post('/sub-users', data),
  update: (userId, data) => api.put(`/sub-users/${userId}`, data),
  delete: (userId) => api.delete(`/sub-users/${userId}`),
  getPermissions: (userId) => api.get(`/sub-users/${userId}/permissions`),
};

// Inventory / Bodega APIs
export const inventoryAPI = {
  getProducts: (propertyId) => api.get(`/inventory/products?property_id=${propertyId}`),
  createProduct: (data) => api.post('/inventory/products', data),
  updateProduct: (productId, data) => api.put(`/inventory/products/${productId}`, data),
  deleteProduct: (productId) => api.delete(`/inventory/products/${productId}`),
  adjustStock: (productId, data) => api.patch(`/inventory/products/${productId}/stock`, data),
  getMovements: (propertyId, productId) => api.get(`/inventory/movements?property_id=${propertyId}${productId ? `&product_id=${productId}` : ''}`),
  getCategories: (propertyId) => api.get(`/inventory/categories/${propertyId}`),
};

// Room Charges / Consumos APIs
export const roomChargeAPI = {
  create: (data) => api.post('/room-charges', data),
  getByReservation: (reservationId) => api.get(`/room-charges/${reservationId}`),
  delete: (chargeId) => api.delete(`/room-charges/${chargeId}`),
  getAccount: (reservationId) => api.get(`/room-account/${reservationId}`),
};

// Facturación APIs
export const facturacionAPI = {
  getConfig: (propertyId) => api.get(`/facturacion/config/${propertyId}`),
  saveConfig: (propertyId, data) => api.put(`/facturacion/config/${propertyId}`, data),
  uploadLogo: (propertyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/facturacion/${propertyId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBillable: (propertyId) => api.get(`/facturacion/${propertyId}/reservas-facturables`),
  getDocs: (propertyId, tipo) => api.get(`/facturacion/${propertyId}/documentos`, { params: { tipo } }),
  createDoc: (propertyId, data) => api.post(`/facturacion/${propertyId}/documentos`, data),
  convertToFactura: (docId) => api.post(`/facturacion/documentos/${docId}/convertir-factura`),
};

export default api;
